import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  Nav, 
  NavItem, 
  NavLink, 
  TabContent, 
  TabPane
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select
import Select, { components } from 'react-select';

// Custom CSS
import "./InfoQuestionSetEditor.css";

// Components
import QuestionEntriesTab from "./InfoQuestionSetEntriesTab";
import QuestionRewardsTab from "./InfoQuestionSetRewardsTab";

// Common Components
import ThumbnailUploader from '../components/ThumbnailUploader';
import HorizontalLoader from 'components/Common/HorizontalLoader';
import CopyQuestionSetModal from "./CopyQuestionSetModal";
import CopyableInfoQuestionsModal from "./CopyableInfoQuestionsModal";

// Custom Hooks and utils
import { useThumbnail } from 'utils/thumbnails/useThumbnail';
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";
import { useEditableQuestionSetFields } from '../utils/useEditableQuestionSetFields';
import { uploadImage, fetchImage } from "utils/images/imageManager";

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";


/*----------------------
/* Main Component
/*----------------------*/

const InfoQuestionSetEditor = ({ t }) => {


  const navigate = useNavigate();
  const { infoQuestionSetId } = useParams();

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [infoQuestionSet, setInfoQuestionSet] = useState(null);
  const [infoQuestionSetEntries, setInfoQuestionSetEntries] = useState([]);
  const [questionSetLoading, setQuestionSetLoading] = useState(true);
  const [error, setError] = useState(null);

  const [imageMap, setImageMap] = useState(new Map());

  const [isCopyTableModalOpen, setIsCopyTableModalOpen] = useState(false);

  const toggleCopyTableModal = () => {
    setIsCopyTableModalOpen(prev => !prev);
  };

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});


  const [activeTab, setActiveTab] = useState("questions");

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };


  //--------------------------------------------------
  // Loading Info on Mount
  //--------------------------------------------------

  const fetchQuestionSetAndEntries = async () => {
    try {
      setQuestionSetLoading(true);
      setError(null);

      // ---- Load Set ----
      const setResponse = await axiosInstance.get(
        `/knovia/infoQuestionSet/${infoQuestionSetId}`
      );
      console.log('questionSet Loaded:', setResponse.data);
      setInfoQuestionSet(setResponse.data);

      // ---- Load All Entries ----
      const questionsResponse = await axiosInstance.get(
        `/knovia/infoQuestionSet/${infoQuestionSetId}/getInfoQuestionSetEntries`
      );
      console.log('infoEntries in Set Loaded:', questionsResponse.data);

      const { questions, sections, textContents, imageContents } = questionsResponse.data;

      const mergedEntries = [
        ...questions.map((q) => ({ ...q, type: "question" })),
        ...sections.map((s) => ({ ...s, type: "section" })),
        ...textContents.map((t) => ({ ...t, type: "text" })),
        ...imageContents.map((i) => ({ ...i, type: "image" })),
      ];

      // Sort by `order`
      const sortedEntries = mergedEntries.sort((a, b) => a.order - b.order);
      setInfoQuestionSetEntries(sortedEntries);

      // ---- Initial Saved Status ----
      const initialSavedStatus = {};
      sortedEntries.forEach(e => {
        initialSavedStatus[e._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

    } catch (err) {
      console.error(err);
      setError("Failed to load data.");
    } finally {
      setQuestionSetLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionSetAndEntries();
  }, [infoQuestionSetId]);

  useEffect(() => {
    const loadImages = async () => {
      const imageEntries = infoQuestionSetEntries.filter(e => e.type === "image");
      const newMap = new Map(imageMap); // preserve existing

      await Promise.all(
        imageEntries.map(async (entry) => {
          const filename = entry.image;
          if (!filename || newMap.has(filename)) return;

          try {
            const url = await fetchImage(filename, "original");
            newMap.set(filename, url);
          } catch (e) {
            console.error("Failed to load image:", filename);
          }
        })
      );

      setImageMap(newMap);
    };

    loadImages();
  }, [infoQuestionSetEntries]);


  //--------------------------------------------------
  // Editing Set label
  //--------------------------------------------------

   const {
    isSaving: isSavingLabel,
    isSaved: isSavedLabel,
    isError: isSavedLabelError,
    handleChange: handleSetLabelChange,
    handleSave: handleSaveLabel
  } = useEditableQuestionSetFields(infoQuestionSet?.label, 'editLabel', 'label', infoQuestionSetId, axiosInstance, setInfoQuestionSet);

  const {
    isSaving: isSavingDescription,
    isSaved: isSavedDescription,
    isError: isSavedDescriptionError,
    handleChange: handleSetDescriptionChange,
    handleSave: handleSaveDescription
  } = useEditableQuestionSetFields(infoQuestionSet?.description, 'editDescription', 'description', infoQuestionSetId, axiosInstance, setInfoQuestionSet);


  //--------------------------------------------------
  // Copying QuestionSet
  //--------------------------------------------------

  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  const toggleCopyModal = () => {
    setIsCopyModalOpen(!isCopyModalOpen);
  };

  const handleCopyToSameTopic = async (onSuccess, onError) => {
    try {
      const res = await axiosInstance.post(`/knovia/infoQuestionSet/${infoQuestionSetId}/copyToTopic`, {
        targetTopicId: infoQuestionSet.topic,
      });
      console.log("Copy result:", res.data);
      onSuccess(); // tell modal it succeeded
    } catch (err) {
      console.error("Copy failed:", err);
      onError();
    }
  };

  const handleCopyToOtherTopics = async (selectedTopicId = null, onSuccess, onError) => {
    if (!selectedTopicId) {
      try {
        const res = await axiosInstance.get(`/knovia/infoQuestionSet/${infoQuestionSetId}/getOtherTopicsInCollection`);
        return res.data;
      } catch (err) {
        console.error("Failed to fetch topics in collection", err);
        return [];
      }
    } else {
      try {
        const res = await axiosInstance.post(`/knovia/infoQuestionSet/${infoQuestionSetId}/copyToTopic`, {
          targetTopicId: selectedTopicId,
        });
        console.log("Copy result:", res.data);
        onSuccess();
      } catch (err) {
        console.error("Copy failed:", err);
        onError();
      }
    }
  };


  //--------------------------------------------------
  // Replace text in QuestionSet
  //--------------------------------------------------
  const [replaceTarget, setReplaceTarget] = useState("");
  const [replaceWith, setReplaceWith] = useState("");
  const [replaceStatus, setReplaceStatus] = useState(null); // null | "saving" | "success" | "error"

  const handleReplaceAll = async () => {
    if (!replaceTarget.trim()) return;

    setReplaceStatus("saving");

    try {
      const res = await axiosInstance.patch(
        `/knovia/infoQuestionSet/${infoQuestionSetId}/replaceAllText`,
        {
          target: replaceTarget,
          replacement: replaceWith,
        }
      );

      setInfoQuestionSetEntries(res.data.updatedEntries.sort((a, b) => a.order - b.order));
      setReplaceStatus("success");
    } catch (err) {
      console.error("Replace failed:", err);
      setReplaceStatus("error");
    }
  };


  //--------------------------------------------------
  // Thumbnails
  //--------------------------------------------------

  const original = useThumbnail("infoQuestionSet", "uploadThumbnail", "viewThumbnail");
  const medium   = useThumbnail("infoQuestionSet", "uploadThumbnail/sizeMedium", "viewThumbnail/sizeMedium");
  const small    = useThumbnail("infoQuestionSet", "uploadThumbnail/sizeSmall", "viewThumbnail/sizeSmall");

  const handleFileChange = (e, uploadFn, size) => {
    const file = e.target.files[0];
    if (file) {
      uploadFn(file, infoQuestionSet, axiosInstance, size);
    }
  };

  useEffect(() => {
    if (infoQuestionSet?._id) {
      original.fetchThumbnail(infoQuestionSet, axiosInstance, "sizeOriginal");
      medium.fetchThumbnail(infoQuestionSet, axiosInstance, "sizeMedium");
      small.fetchThumbnail(infoQuestionSet, axiosInstance, "sizeSmall");
    }
  }, [infoQuestionSet?._id]);
  

  /*
  useEffect(()=>{
    console.log('imageMap:', imageMap);
  },[imageMap])
  */



  //--------------------------------------------------
  // Render logic (Not in Use)
  //-------------------------------------------------- 
  /*
  if (questionSetLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!infoQuestionSet) return <div>No InfoQuestionSet found.</div>;
  */



  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              <Row>
                <Col xs={12} md={4}>
                  <CardTitle className="mb-4">{t("編輯題組")}</CardTitle>
                </Col>
                <Col xs={12} md={8} 
                  className="d-flex flex-column mb-2 mb-md-0
                    flex-md-row align-items-end 
                    align-items-md-center justify-content-md-end"
                >
                  <div className="mb-1 mb-md-0">
                    <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                      onClick={(e) => { navigate(`/editInfoTopics/${infoQuestionSet.topic}`); }}
                    >
                      <i className='bx bx-arrow-back' ></i>{` 回到主題`}
                    </Button>
                    <Button color="primary" style={{ marginRight: "0.3rem" }}
                      onClick={() => setIsCopyModalOpen(true)}
                    >
                      複製題組
                    </Button>
                  </div>
                  <div>
                    <Button color="warning" style={{ marginRight: "0.3rem" }}>
                      開始測試填答
                    </Button>
                    <Button color="success" style={{ marginRight: "0.3rem" }}
                      onClick={toggleCopyTableModal}
                    >
                      複製貼上到 Excel
                    </Button>
                  </div>
                </Col>
              </Row>

              {/*
              <Input
                type="file"
                onChange={(e) => handleFileChange(e)}
              />
              <div style={{ marginTop: "1rem" }}>
                <img
                  src={testImageUrl || placeholderSmall}
                  alt="Uploaded Preview"
                  style={{ maxWidth: "300px", borderRadius: "8px" }}
                />
              </div>
              */}

              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {/* QuestionSet Label Field */}
                  <Row className="mb-3">

                    <Col xs={12} sm={8} xl={6}>
                      <Row>

                        <Col xs={12} md={4}>
                          <h4>題組名稱:</h4>
                        </Col>

                        <Col xs={8} md={8} className="d-flex gap-3 flex-row">
                          <Input
                            type="text"
                            value={infoQuestionSet?.label || ""}
                            onChange={handleSetLabelChange}
                          />
                          <Button color="outline-success" onClick={()=>{handleSaveLabel(infoQuestionSet?.label)}}
                            style={{ padding: "7px 8px" }}
                          >
                            <i className="bx bx-check" />
                          </Button>
                        </Col>

                        {/* Label edit status */}
                        <Col xs={4} md={12} className="d-flex align-items-center justify-content-start">
                          {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                          {!isSavingLabel && !isSavedLabelError &&
                            <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                          }
                          {!isSavingLabel && isSavedLabelError &&
                            <span style={{ color: "red" }}>Error Saving Label!</span>
                          }
                        </Col>
                      </Row>
                    </Col>

                  </Row>

                  {/* QuestionSet Description Field */}
                  <Row className="mb-3">
                    <Col xs={12} sm={8} xl={6}>
                      <Row>

                        <Col xs={12} md={4}>
                          <h4>題組描述:</h4>
                        </Col>

                        <Col xs={8} md={8} className="d-flex gap-3 flex-row">
                          <Input
                            type="text"
                            value={infoQuestionSet?.description || ""}
                            onChange={handleSetDescriptionChange}
                          />
                          <Button color="outline-success" onClick={()=>{handleSaveDescription(infoQuestionSet?.description)}}
                            style={{ padding: "7px 8px" }}
                          >
                            <i className="bx bx-check" />
                          </Button>
                        </Col>

                        {/* Label edit status */}
                        <Col xs={4} md={12} className="d-flex align-items-center justify-content-start">
                          {isSavingDescription && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                          {!isSavingDescription && !isSavedDescriptionError &&
                            <span style={{ color: isSavedDescription ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                          }
                          {!isSavingDescription && isSavedDescriptionError &&
                            <span style={{ color: "red" }}>Error Saving Label!</span>
                          }
                        </Col>
                      </Row>
                    </Col>

                  </Row>

                  {/* Question Set thumbnails */}
                  <Row>
                    <Col xs={12}>
                      <h4>封面圖片:</h4>
                    </Col>
                  </Row>

                  <ThumbnailUploader
                    original={original}
                    medium={medium}
                    small={small}
                    handleFileChange={handleFileChange}
                  />
  
                  <hr />

                  {/* Replace */}
                  <Row style={{ margin: "1.5rem 0" }}>
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                      <Input
                        type="text"
                        placeholder="A"
                        value={replaceTarget}
                        onChange={(e) => setReplaceTarget(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                      <Input
                        type="text"
                        placeholder="B"
                        value={replaceWith}
                        onChange={(e) => setReplaceWith(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} md={4} className="mb-2 mb-md-0">
                      <Button color="danger" onClick={handleReplaceAll} style={{ marginRight: "1rem" }}>
                        全部取代 A->B
                      </Button>
                      {replaceStatus === "saving" && <span style={{ color: "#f1b34c" }}>Replacing...</span>}
                      {replaceStatus === "success" && <span style={{ color: "#34c38f" }}>✔ Replaced</span>}
                      {replaceStatus === "error" && <span style={{ color: "red" }}>Error replacing!</span>}
                    </Col>
                    <Col md={4}></Col>
                  </Row>

                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink
                        className={activeTab === "questions" ? "active" : ""}
                        onClick={() => toggleTab("questions")}
                        style={{ cursor: "pointer" }}
                      >
                        題組中的問題
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "other" ? "active" : ""}
                        onClick={() => toggleTab("reward")}
                        style={{ cursor: "pointer" }}
                      >
                        題組獎勵
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="questions">
                      <QuestionEntriesTab
                        infoQuestionSetId={infoQuestionSetId}
                        infoQuestionSetEntries={infoQuestionSetEntries}
                        setInfoQuestionSetEntries={setInfoQuestionSetEntries}
                        isSavingStatus={isSavingStatus}
                        setIsSavingStatus={setIsSavingStatus}
                        isSavedStatus={isSavedStatus}
                        setIsSavedStatus={setIsSavedStatus}
                        imageMap={imageMap}
                        setImageMap={setImageMap}
                        navigate={navigate}
                        setError={setError}
                      />
                    </TabPane>

                    <TabPane tabId="reward">
                      <QuestionRewardsTab
                        infoQuestionSetId={infoQuestionSetId}
                        infoQuestionSet={infoQuestionSet}
                        setInfoQuestionSet={setInfoQuestionSet}
                        setError={setError}
                      />
                    </TabPane>
                  </TabContent>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>

      <CopyQuestionSetModal
        isOpen={isCopyModalOpen}
        toggle={toggleCopyModal}
        onCopyToSameTopic={handleCopyToSameTopic}
        onCopyToOtherTopics={handleCopyToOtherTopics}
      />

      <CopyableInfoQuestionsModal
        isOpen={isCopyTableModalOpen}
        toggle={toggleCopyTableModal}
        infoQuestionSet={infoQuestionSet}
        infoQuestionSetEntries={infoQuestionSetEntries}
      />

      

    </React.Fragment>
  );
}

export default withTranslation()(InfoQuestionSetEditor);
